import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getSuperFeedbacks } from "../../../redux/actions/profile";

const PRIMARY = "#a7cc48";
const BG = "#2c2d3f";
const SECTORS = [1, 2, 3, 4, 5];

export default function AllFeedbacks() {
  const dispatch = useDispatch();
  const feedbacks = useSelector((s) => s.profile.superFeedbacks || []);
  const [search, setSearch] = useState("");
  const [sector, setSector] = useState("all");

  useEffect(() => { dispatch(getSuperFeedbacks()); }, [dispatch]);

  const filtered = feedbacks.filter((f) => {
    const q = search.toLowerCase();
    const reporterName = f.reporter?.fullName || "";
    const matchSearch = !q ||
      (f.report || "").toLowerCase().includes(q) ||
      reporterName.toLowerCase().includes(q) ||
      (f.location || "").toLowerCase().includes(q);
    const matchSector = sector === "all" || String(f.sector) === sector;
    return matchSearch && matchSector;
  });

  const sectorCounts = {};
  feedbacks.forEach((f) => {
    const s = String(f.sector);
    sectorCounts[s] = (sectorCounts[s] || 0) + 1;
  });

  return (
    <div style={S.root}>
      <div style={S.header}>
        <div>
          <h2 style={S.title}>All Feedbacks</h2>
          <p style={S.sub}>Agent field reports from across the platform</p>
        </div>
        <span style={S.badge}>{feedbacks.length} reports</span>
      </div>

      <div style={S.toolbar}>
        <div style={S.searchWrap}>
          <i className="bi bi-search" style={S.searchIcon} />
          <input
            style={S.search}
            placeholder="Search by report, agent, or location…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div style={S.tabs}>
          <button style={{ ...S.tab, ...(sector === "all" ? S.tabActive : {}) }} onClick={() => setSector("all")}>
            All ({feedbacks.length})
          </button>
          {SECTORS.map((n) => (
            <button key={n} style={{ ...S.tab, ...(sector === String(n) ? S.tabActive : {}) }} onClick={() => setSector(String(n))}>
              S{n} ({sectorCounts[String(n)] || 0})
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div style={S.empty}>
          <i className="bi bi-chat-left-text" style={{ fontSize: 36, color: "#ddd" }} />
          <p style={{ color: "#bbb", marginTop: 12 }}>No feedbacks found</p>
        </div>
      ) : (
        <div style={S.grid}>
          {filtered.map((f, i) => (
            <FeedbackCard key={f._id || i} feedback={f} />
          ))}
        </div>
      )}
    </div>
  );
}

function FeedbackCard({ feedback: f }) {
  const [expanded, setExpanded] = useState(false);
  const agent = f.reporter;
  const mapSrc = f.location ? `https://maps.google.com/maps?q=${encodeURIComponent(f.location)}&output=embed` : null;

  return (
    <div style={S.card}>
      <div style={S.cardTop}>
        <div style={S.cardLeft}>
          <span style={S.sectorTag}>Sector {f.sector}</span>
          <span style={S.dateTag}>{f.date ? new Date(f.date).toLocaleDateString() : "—"}</span>
        </div>
        {f.location && (
          <button style={S.mapToggle} onClick={() => setExpanded(!expanded)}>
            <i className={`bi bi-${expanded ? "chevron-up" : "geo-alt"}`} />
            {expanded ? " Hide" : " Map"}
          </button>
        )}
      </div>

      <p style={S.report}>{f.report}</p>

      {f.location && (
        <div style={S.locationRow}>
          <i className="bi bi-geo-alt-fill" style={{ color: PRIMARY, fontSize: 12 }} />
          <span style={S.locationText}>{f.location}</span>
        </div>
      )}

      {expanded && mapSrc && (
        <iframe title="map" src={mapSrc} style={S.map} frameBorder="0" allowFullScreen />
      )}

      {agent && (
        <div style={S.agentRow}>
          <div style={S.agentAvatar}>{(agent.fullName || "?")[0].toUpperCase()}</div>
          <div>
            <div style={S.agentName}>{agent.fullName || "—"}</div>
            <div style={S.agentMeta}>{agent.email} · {agent.agentCode}</div>
          </div>
        </div>
      )}
    </div>
  );
}

const S = {
  root: { paddingBottom: 40 },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 24 },
  title: { fontSize: 22, fontWeight: 800, color: BG, margin: 0 },
  sub: { fontSize: 13, color: "#888", marginTop: 4 },
  badge: { background: "rgba(167,204,72,0.12)", color: "#5a8a00", border: "1px solid " + PRIMARY, borderRadius: 20, padding: "5px 14px", fontSize: 12, fontWeight: 700 },
  toolbar: { display: "flex", gap: 12, alignItems: "center", marginBottom: 20, flexWrap: "wrap" },
  searchWrap: { position: "relative", flex: 1, minWidth: 220 },
  searchIcon: { position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#bbb", fontSize: 14 },
  search: { width: "100%", padding: "9px 12px 9px 36px", borderRadius: 8, border: "1.5px solid #eee", fontSize: 13, outline: "none", boxSizing: "border-box" },
  tabs: { display: "flex", gap: 6, flexWrap: "wrap" },
  tab: { padding: "6px 12px", borderRadius: 20, border: "1.5px solid #e0e0e0", background: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", color: "#888" },
  tabActive: { background: PRIMARY, borderColor: PRIMARY, color: "#fff" },
  empty: { textAlign: "center", padding: "60px 0" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 16 },
  card: { background: "#fff", borderRadius: 10, padding: "16px 18px", boxShadow: "0 2px 10px rgba(0,0,0,0.06)" },
  cardTop: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 },
  cardLeft: { display: "flex", gap: 8 },
  sectorTag: { background: "rgba(167,204,72,0.12)", color: "#5a8a00", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700 },
  dateTag: { background: "#f5f5f5", color: "#aaa", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 600 },
  mapToggle: { background: "none", border: "1px solid #e0e0e0", borderRadius: 20, padding: "3px 10px", fontSize: 11, cursor: "pointer", color: "#888", display: "flex", alignItems: "center", gap: 4 },
  report: { fontSize: 14, color: "#333", lineHeight: 1.6, margin: "0 0 10px" },
  locationRow: { display: "flex", alignItems: "center", gap: 5, marginBottom: 8 },
  locationText: { fontSize: 12, color: "#888" },
  map: { width: "100%", height: 180, borderRadius: 8, border: "none", marginBottom: 10 },
  agentRow: { display: "flex", alignItems: "center", gap: 10, paddingTop: 10, borderTop: "1px solid #f5f5f5" },
  agentAvatar: { width: 32, height: 32, borderRadius: "50%", background: PRIMARY, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 14, flexShrink: 0 },
  agentName: { fontSize: 13, fontWeight: 700, color: BG },
  agentMeta: { fontSize: 11, color: "#aaa" },
};
